import { useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useIntl } from 'react-intl'

import { QUOTES_INSTANCES } from 'data/gradoo/queries/quotes'
import useAuth from 'data/gradoo/hooks/useAuth'
import {
  DragOverlay,
  Draggable,
  useContentDnd
} from 'screens/YearbookEditor/providers/ContentDndProvider'
import { Ids } from 'screens/YearbookEditor/providers/ContentDndProvider/types'
import useContentMarks, {
  ContentMarkType
} from 'shared/hooks/useContentMarks'
import { QuoteFragment } from 'shared/types/gradoo/graphql'
import Flex from 'shared/ui/Flex'
import SearchInput from 'shared/ui/SearchInput'
import TabBar from 'shared/ui/TabBar'

import QuoteItem from './QuoteItem'
import Skeleton from './Skeleton'

const PAGE_SIZE = Infinity // TODO: lower back to 20 and fix infinite scroll
type QuotesPanelProps = {
  moduleInstanceId?: string
}
const QuotesPanel: React.FC<QuotesPanelProps> = ({
  moduleInstanceId
}) => {
  const [activeTab, setActiveTab] = useState(0)
  const [search, setSearch] = useState('')
  const { formatMessage } = useIntl()
  const { authGroupId } = useAuth()

  const { markedIds, toggleMark } = useContentMarks({
    groupId: authGroupId ?? undefined,
    moduleInstanceId
  })

  const { data: quotesData, fetchMore: fetchMoreQuotes } = useQuery(
    QUOTES_INSTANCES,
    {
      skip: !moduleInstanceId,
      variables: {
        moduleInstance: moduleInstanceId,
        orderBy: activeTab === 0 ? '-created' : '-likes',
        first: PAGE_SIZE
      },
      notifyOnNetworkStatusChange: true,
      context: { client: 'gradoo' }
    }
  )

  const quotes: QuoteFragment[] =
    (quotesData?.quotesInstances?.edges.map(
      edge => edge?.node
    ) as QuoteFragment[]) || []

  const filteredQuotes = useMemo(() => {
    const term = search.toLocaleLowerCase()
    return quotes.filter(
      q =>
        q.quote.toLocaleLowerCase().includes(term) ||
        q.userGroup.user.firstName
          .toLocaleLowerCase()
          .includes(term) ||
        q.userGroup.user.lastName.toLocaleLowerCase().includes(term)
    )
  }, [quotes, search])

  const hasNextPage =
    quotesData?.quotesInstances?.pageInfo.hasNextPage || false
  const endCursor = quotesData?.quotesInstances?.pageInfo.endCursor

  const onLoadMoreQuotes = async () => {
    if (!hasNextPage) {
      return
    }
    await fetchMoreQuotes({
      variables: {
        moduleInstance: moduleInstanceId,
        orderBy: activeTab === 0 ? '-created' : '-likes',
        after: endCursor
      }
    })
  }

  const { draggingContent } = useContentDnd()

  return (
    <Flex direction="column">
      <SearchInput
        value={search}
        onChange={setSearch}
        marginBottom={26}
      />
      <Flex marginTop={2} direction="column">
        <TabBar
          activeIndex={activeTab}
          onChange={setActiveTab}
          tabs={[
            formatMessage({ id: 'Panel.quotes.tabs.newest' }),
            formatMessage({ id: 'Panel.quotes.tabs.mostPopular' })
          ]}
        />
        <InfiniteScroll
          dataLength={quotes.length}
          next={onLoadMoreQuotes}
          hasMore={hasNextPage}
          loader={<Skeleton />}
          scrollThreshold={0.8}
          height={window.innerHeight}
        >
          <Flex marginTop={32} direction="column" rowGap={8}>
            {filteredQuotes.map((quote, i) => {
              const isMarked = markedIds.has(quote.id)
              return (
                <Draggable
                  key={quote.id + i}
                  id={quote.id}
                  type={Ids.quote}
                  onDragStart={() =>
                    draggingContent.setQuote(quote)
                  }
                >
                  <QuoteItem
                    data-testid={`QuoteItem:${quote.id}`}
                    quote={quote}
                    isMarked={isMarked}
                    onToggleMark={() =>
                      toggleMark(quote.id, ContentMarkType.Quote)
                    }
                  />
                </Draggable>
              )
            })}

            <DragOverlay>
              <QuoteItem quote={draggingContent.quote} />
            </DragOverlay>
          </Flex>
        </InfiniteScroll>
      </Flex>
    </Flex>
  )
}

export default QuotesPanel
